import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import PushNotificationSettings from '../../../components/pwa/PushNotificationSettings';
import proactiveDigestsService from '../../../services/proactiveDigestsService';
import organizationsService from '../../../services/crm/organizationsService';
import { useCrmOrgStore } from '../../../store/crmOrg.store';
import { useAuth } from '../../../contexts/AuthContext';

interface SettingsTabProps {
  // Reserved for future use
}

const SettingsTab: React.FC<SettingsTabProps> = () => {
  const [enableTTS, setEnableTTS] = useState(false);
  const [autoSendVoice, setAutoSendVoice] = useState(false);
  const [useBusinessContext, setUseBusinessContext] = useState(true);
  const { user } = useAuth();
  const { activeOrgId, setActiveOrgId, setCurrentUserId } = useCrmOrgStore();

  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestTimezone, setDigestTimezone] = useState('UTC');
  const [digestMorningEnabled, setDigestMorningEnabled] = useState(true);
  const [digestMorningTime, setDigestMorningTime] = useState('08:00');
  const [digestLunchEnabled, setDigestLunchEnabled] = useState(false);
  const [digestLunchTime, setDigestLunchTime] = useState('12:30');
  const [digestEmailEnabled, setDigestEmailEnabled] = useState(true);
  const [digestInAppEnabled, setDigestInAppEnabled] = useState(true);
  const [digestDealAlertsEnabled, setDigestDealAlertsEnabled] = useState(true);
  const [digestNewsTopicsText, setDigestNewsTopicsText] = useState('');

  const [digestRolePreset, setDigestRolePreset] = useState<'solo_founder' | 'sales_led_smb' | 'agency' | 'ecom'>('sales_led_smb');
  const [digestQuietHoursEnabled, setDigestQuietHoursEnabled] = useState(false);
  const [digestQuietHoursStart, setDigestQuietHoursStart] = useState('22:00');
  const [digestQuietHoursEnd, setDigestQuietHoursEnd] = useState('07:00');
  const [digestDealAlertMinAmount, setDigestDealAlertMinAmount] = useState('');
  const [digestNewsMaxPerWeek, setDigestNewsMaxPerWeek] = useState('3');

  const [digestTesting, setDigestTesting] = useState(false);

  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsSaving, setTargetsSaving] = useState(false);
  const [userMonthlyRevenueTarget, setUserMonthlyRevenueTarget] = useState('');
  const [userWeeklyMeetingsTarget, setUserWeeklyMeetingsTarget] = useState('');
  const [orgMonthlyRevenueTarget, setOrgMonthlyRevenueTarget] = useState('');
  const [orgWeeklyMeetingsTarget, setOrgWeeklyMeetingsTarget] = useState('');

  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
  }, [user?.id, setCurrentUserId]);

  useEffect(() => {
    const bootstrapOrganizations = async () => {
      if (!user?.id) return;
      if (activeOrgId) return;

      try {
        const response = await organizationsService.list();
        const list = response.data || [];
        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          const createdOrg = created.data;
          if (createdOrg) {
            setActiveOrgId(createdOrg.id);
          }
        } else {
          setActiveOrgId(list[0]?.id ?? null);
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        toast.error(err?.message || 'Failed to load organizations');
      }
    };

    void bootstrapOrganizations();
  }, [user?.id, activeOrgId, setActiveOrgId]);

  useEffect(() => {
    // Load settings from localStorage
    try {
      const tts = localStorage.getItem('bc_enable_tts');
      const voice = localStorage.getItem('bc_auto_send_voice');
      const context = localStorage.getItem('bc_use_business_context');
      
      if (tts) setEnableTTS(tts === 'true');
      if (voice) setAutoSendVoice(voice === 'true');
      if (context) setUseBusinessContext(context === 'true');
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, []);

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) setDigestTimezone(tz);

    const load = async () => {
      setDigestLoading(true);
      try {
        const res = await proactiveDigestsService.getSettings();
        const s = res?.data;
        if (s) {
          setDigestEnabled(!!s.enabled);
          setDigestTimezone(s.timezone || tz || 'UTC');
          if (!activeOrgId && s.organization_id) {
            setActiveOrgId(s.organization_id);
          }
          setDigestMorningEnabled(!!s.morning_enabled);
          setDigestMorningTime(s.morning_time || '08:00');
          setDigestLunchEnabled(!!s.lunch_enabled);
          setDigestLunchTime(s.lunch_time || '12:30');
          setDigestEmailEnabled(!!s.email_enabled);
          setDigestInAppEnabled(!!s.in_app_enabled);
          setDigestDealAlertsEnabled(!!s.deal_alerts_enabled);
          setDigestNewsTopicsText(Array.isArray(s.news_topics) ? s.news_topics.join(', ') : '');

          const rp = (s as any).role_preset;
          if (rp === 'solo_founder' || rp === 'sales_led_smb' || rp === 'agency' || rp === 'ecom') {
            setDigestRolePreset(rp);
          }
          setDigestQuietHoursEnabled(!!(s as any).quiet_hours_enabled);
          setDigestQuietHoursStart(String((s as any).quiet_hours_start || '22:00'));
          setDigestQuietHoursEnd(String((s as any).quiet_hours_end || '07:00'));
          const minAmt = (s as any).deal_alert_min_amount;
          setDigestDealAlertMinAmount(minAmt === null || minAmt === undefined ? '' : String(minAmt));
          const nmax = (s as any).news_max_per_week;
          setDigestNewsMaxPerWeek(nmax === null || nmax === undefined ? '3' : String(nmax));
        }
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load proactive digest settings');
      } finally {
        setDigestLoading(false);
      }
    };

    void load();
  }, [activeOrgId, setActiveOrgId]);

  useEffect(() => {
    if (!activeOrgId) return;

    const loadTargets = async () => {
      setTargetsLoading(true);
      try {
        const res = await proactiveDigestsService.getTargets(activeOrgId);
        const org = res?.data?.org;
        const user = res?.data?.user;
        setOrgMonthlyRevenueTarget(org && typeof org.monthly_revenue_target === 'number' ? String(org.monthly_revenue_target) : '');
        setOrgWeeklyMeetingsTarget(org && typeof org.weekly_meetings_target === 'number' ? String(org.weekly_meetings_target) : '');
        setUserMonthlyRevenueTarget(user && user.monthly_revenue_target !== null && user.monthly_revenue_target !== undefined ? String(user.monthly_revenue_target) : '');
        setUserWeeklyMeetingsTarget(user && user.weekly_meetings_target !== null && user.weekly_meetings_target !== undefined ? String(user.weekly_meetings_target) : '');
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load targets');
      } finally {
        setTargetsLoading(false);
      }
    };

    void loadTargets();
  }, [activeOrgId]);

  const handleSaveSetting = (key: string, value: boolean) => {
    try {
      localStorage.setItem(key, String(value));
      toast.success('Setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    }
  };

  const saveTargets = async () => {
    if (!activeOrgId) {
      toast.error('Select an active CRM workspace to save targets');
      return;
    }

    const parseOptionalNumber = (raw: string): number | undefined => {
      const trimmed = String(raw || '').trim();
      if (!trimmed) return undefined;
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) return undefined;
      return n;
    };

    setTargetsSaving(true);
    try {
      const userMonthly = parseOptionalNumber(userMonthlyRevenueTarget);
      const userWeekly = parseOptionalNumber(userWeeklyMeetingsTarget);
      await proactiveDigestsService.updateUserTargets({
        organization_id: activeOrgId,
        ...(userMonthly !== undefined ? { monthly_revenue_target: userMonthly } : {}),
        ...(userWeekly !== undefined ? { weekly_meetings_target: userWeekly } : {}),
      });

      const orgMonthly = parseOptionalNumber(orgMonthlyRevenueTarget);
      const orgWeekly = parseOptionalNumber(orgWeeklyMeetingsTarget);
      if (orgMonthly !== undefined || orgWeekly !== undefined) {
        await proactiveDigestsService.updateOrgTargets({
          organization_id: activeOrgId,
          ...(orgMonthly !== undefined ? { monthly_revenue_target: orgMonthly } : {}),
          ...(orgWeekly !== undefined ? { weekly_meetings_target: orgWeekly } : {}),
        });
      }

      toast.success('Targets saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save targets');
    } finally {
      setTargetsSaving(false);
    }
  };

  const saveDigestSettings = async (): Promise<boolean> => {
    setDigestSaving(true);
    try {
      const topics = digestNewsTopicsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 5);

      const parseOptionalNumber = (raw: string): number | undefined => {
        const trimmed = String(raw || '').trim();
        if (!trimmed) return undefined;
        const n = Number(trimmed);
        if (!Number.isFinite(n) || n < 0) return undefined;
        return n;
      };

      const minAmt = parseOptionalNumber(digestDealAlertMinAmount);
      const newsMax = parseOptionalNumber(digestNewsMaxPerWeek);
      await proactiveDigestsService.updateSettings({
        enabled: digestEnabled,
        timezone: digestTimezone,
        ...(activeOrgId ? { organization_id: activeOrgId } : {}),
        morning_enabled: digestMorningEnabled,
        morning_time: digestMorningTime,
        lunch_enabled: digestLunchEnabled,
        lunch_time: digestLunchTime,
        email_enabled: digestEmailEnabled,
        in_app_enabled: digestInAppEnabled,
        deal_alerts_enabled: digestDealAlertsEnabled,
        news_topics: topics,
        role_preset: digestRolePreset,
        quiet_hours_enabled: digestQuietHoursEnabled,
        quiet_hours_start: digestQuietHoursStart,
        quiet_hours_end: digestQuietHoursEnd,
        ...(minAmt !== undefined ? { deal_alert_min_amount: minAmt } : {}),
        ...(newsMax !== undefined ? { news_max_per_week: Math.floor(newsMax) } : {})
      });
      toast.success('Proactive digest settings saved');
      return true;
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save proactive digest settings');
      return false;
    } finally {
      setDigestSaving(false);
    }
  };

  const testDigest = async (digestType: 'morning' | 'lunch' | 'news') => {
    setDigestTesting(true);
    try {
      if (digestType === 'news') {
        const ok = await saveDigestSettings();
        if (!ok) return;
      }
      const res = await proactiveDigestsService.createTestRun({ digest_type: digestType });
      const id = res?.data?.id;
      if (!id) {
        toast.error('Unable to generate test digest');
        return;
      }
      window.location.href = `/digests/${id}`;
    } catch (e: any) {
      toast.error(e?.message || 'Unable to generate test digest');
    } finally {
      setDigestTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PushNotificationSettings showInline={true} />

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900">Proactive Digests</h4>
          <button
            onClick={() => void saveDigestSettings()}
            disabled={digestLoading || digestSaving}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {digestSaving ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => void testDigest('morning')}
            disabled={digestLoading || digestSaving || digestTesting}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {digestTesting ? 'Generating…' : 'Test morning digest'}
          </button>
          <button
            type="button"
            onClick={() => void testDigest('lunch')}
            disabled={digestLoading || digestSaving || digestTesting}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {digestTesting ? 'Generating…' : 'Test lunch digest'}
          </button>
          <button
            type="button"
            onClick={() => void testDigest('news')}
            disabled={digestLoading || digestSaving || digestTesting}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {digestTesting ? 'Generating…' : 'Test news digest'}
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Enable digests</p>
              <p className="text-sm text-gray-500">Daily updates delivered in-app and/or by email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={digestEnabled}
                onChange={(e) => setDigestEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <input
                value={digestTimezone}
                onChange={(e) => setDigestTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Europe/London"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role preset</label>
              <select
                value={digestRolePreset}
                onChange={(e) => setDigestRolePreset(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="solo_founder">Solo founder</option>
                <option value="sales_led_smb">Sales-led SMB</option>
                <option value="agency">Agency</option>
                <option value="ecom">E-commerce</option>
              </select>
            </div>
          </div>

          <details className="rounded-md border border-gray-200 p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-800">Advanced controls</summary>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
                <div>
                  <p className="font-medium text-gray-700">Quiet hours</p>
                  <p className="text-sm text-gray-500">During quiet hours, alerts are bundled and delivered after quiet hours end.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={digestQuietHoursEnabled}
                    onChange={(e) => setDigestQuietHoursEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quiet hours start</label>
                  <input
                    type="time"
                    value={digestQuietHoursStart}
                    onChange={(e) => setDigestQuietHoursStart(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quiet hours end</label>
                  <input
                    type="time"
                    value={digestQuietHoursEnd}
                    onChange={(e) => setDigestQuietHoursEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal alert minimum amount (£)</label>
                  <input
                    value={digestDealAlertMinAmount}
                    onChange={(e) => setDigestDealAlertMinAmount(e.target.value)}
                    inputMode="decimal"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">Only alert on Closed Won deals at or above this amount.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">News digests per week</label>
                  <input
                    value={digestNewsMaxPerWeek}
                    onChange={(e) => setDigestNewsMaxPerWeek(e.target.value)}
                    inputMode="numeric"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                  />
                  <p className="mt-1 text-xs text-gray-500">Set to 0 to disable news digests.</p>
                </div>
              </div>
            </div>
          </details>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">Morning digest</p>
                  <p className="text-sm text-gray-500">Daily morning briefing</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={digestMorningEnabled}
                    onChange={(e) => setDigestMorningEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  value={digestMorningTime}
                  onChange={(e) => setDigestMorningTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">Lunchtime digest</p>
                  <p className="text-sm text-gray-500">Second daily briefing</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={digestLunchEnabled}
                    onChange={(e) => setDigestLunchEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  value={digestLunchTime}
                  onChange={(e) => setDigestLunchTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
              <div>
                <p className="font-medium text-gray-700">Email delivery</p>
                <p className="text-sm text-gray-500">Send to your account email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={digestEmailEnabled}
                  onChange={(e) => setDigestEmailEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
              <div>
                <p className="font-medium text-gray-700">In-app delivery</p>
                <p className="text-sm text-gray-500">Show in notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={digestInAppEnabled}
                  onChange={(e) => setDigestInAppEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
            <div>
              <p className="font-medium text-gray-700">Deal alerts</p>
              <p className="text-sm text-gray-500">Send an alert when deals move to Closed Won</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={digestDealAlertsEnabled}
                onChange={(e) => setDigestDealAlertsEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="rounded-md border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium text-gray-700">News topics</p>
                <p className="text-sm text-gray-500">Comma-separated (max 5). Example: Premier League, Rangers FC</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDigestNewsTopicsText('Premier League, Rangers FC, Celtic FC')}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Use presets
                </button>
                <button
                  type="button"
                  onClick={() => setDigestNewsTopicsText('')}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>
            <input
              value={digestNewsTopicsText}
              onChange={(e) => setDigestNewsTopicsText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Premier League, Rangers FC, Celtic FC"
            />
          </div>

          <div className="rounded-md border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium text-gray-700">Targets</p>
                <p className="text-sm text-gray-500">Used for progress calculations in digests</p>
              </div>
              <button
                onClick={() => void saveTargets()}
                disabled={targetsLoading || targetsSaving}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {targetsSaving ? 'Saving…' : 'Save targets'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">My targets</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Monthly revenue target</label>
                    <input
                      value={userMonthlyRevenueTarget}
                      onChange={(e) => setUserMonthlyRevenueTarget(e.target.value)}
                      inputMode="decimal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 20000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Weekly meetings target</label>
                    <input
                      value={userWeeklyMeetingsTarget}
                      onChange={(e) => setUserWeeklyMeetingsTarget(e.target.value)}
                      inputMode="numeric"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 5"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Org targets</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Monthly revenue target</label>
                    <input
                      value={orgMonthlyRevenueTarget}
                      onChange={(e) => setOrgMonthlyRevenueTarget(e.target.value)}
                      inputMode="decimal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 100000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Weekly meetings target</label>
                    <input
                      value={orgWeeklyMeetingsTarget}
                      onChange={(e) => setOrgWeeklyMeetingsTarget(e.target.value)}
                      inputMode="numeric"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h4 className="font-medium text-gray-900 mb-4">Voice & Audio</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Text-to-Speech</p>
              <p className="text-sm text-gray-500">Hear responses read aloud</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableTTS}
                onChange={(e) => {
                  setEnableTTS(e.target.checked);
                  handleSaveSetting('bc_enable_tts', e.target.checked);
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Auto-send Voice Messages</p>
              <p className="text-sm text-gray-500">Automatically send after voice input</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoSendVoice}
                onChange={(e) => {
                  setAutoSendVoice(e.target.checked);
                  handleSaveSetting('bc_auto_send_voice', e.target.checked);
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h4 className="font-medium text-gray-900 mb-4">Business Context</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Use Business Context</p>
              <p className="text-sm text-gray-500">Include business data in conversations</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useBusinessContext}
                onChange={(e) => {
                  setUseBusinessContext(e.target.checked);
                  handleSaveSetting('bc_use_business_context', e.target.checked);
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
